import { createFileRoute } from "@tanstack/react-router"
import TabsList from "../../../components/Common/TabsList"
import { Avatar, Flex, SkeletonText, Table, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import ActionsMenu from "../../../components/Common/ActionsMenu"
import Navbar from "../../../components/Common/Navbar"
import { lazy, useMemo } from "react"
import { userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { readSettingsMimetypes } from "../../../api/mimetypes"

const AddSettingsMimeType = lazy(() => import("../../../components/MimeTypes/AddSettingsMimeType"))

export const Route = createFileRoute("/_layout/settings/mimetypes")({
  component: MimeTypes,
})


function MimeTypeTable() {
  // Permission check
  const [mt_create, mt_read, mt_delete] = useMemo(() => {
    return ["create", "read", "delete"].map(
      (action) => userHasPermission([{ perm: `settings_mimetypes_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!mt_read) return <NoPermission/>

  const fields = [
    { title: "Mime-Type", col: null, align: undefined },
    { title: "Endung", col: null, align: undefined },
    { title: "Erstellungsdatum", col: null, align: undefined },
    { title: "Bearbeitungsdatum", col: null, align: undefined },
    { title: "Erstellt Von", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  // Query
  const {
    data: mimetypes,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...readSettingsMimetypes(),
    placeholderData: (prevData) => prevData,
  })

  return (
    <>
      <Navbar
        type="Dateityp"
        addAllowed={mt_create}
        addModalAs={AddSettingsMimeType}
        showSearch={false}
      />

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader textAlign={field.align} key={i}>
                    {field.title}
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader textAlign={field.align} key={field.title}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(4).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!mimetypes?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center">
                    Keine Dateitypen verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                mimetypes?.data.map((item) => (
                  <Table.Row key={item.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell>
                      {item.type_name}
                    </Table.Cell>

                    <Table.Cell>
                      {item.description}
                    </Table.Cell>

                    <Table.Cell>
                      {new Date(`${item.created_at}`).toLocaleString("de-CH", {
                        timeZone: "Europe/Zurich",
                      })}
                    </Table.Cell>

                    <Table.Cell>
                      {item.edited_at &&
                        new Date(`${item.edited_at}`).toLocaleString("de-CH", {
                          timeZone: "Europe/Zurich",
                        })
                      }
                    </Table.Cell>

                    <Table.Cell>
                      <Flex alignItems="center">
                        <Avatar.Root mr={2}>
                          <Avatar.Fallback
                            name={
                              item.fk_last_edited_by.lastname && item.fk_last_edited_by.firstname
                                ? `${item.fk_last_edited_by.lastname} ${item.fk_last_edited_by.firstname}`
                                : item.fk_last_edited_by.firstname || item.fk_last_edited_by.lastname || item.fk_last_edited_by.email
                            }
                          />
                          <Avatar.Image src={item.fk_last_edited_by.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                        </Avatar.Root>
                        <Text>{item.fk_last_edited_by.email}</Text>
                      </Flex>
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Dateityp"
                        value={item}
                        disableDelete={!mt_delete}
                        hideEdit={true}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
    </>
  )
}

export default MimeTypeTable

function MimeTypes() {
  const tabs = useMemo(() => [
    { value: "settings", title: "Allgemein" },
    { value: "settings/navigation", title: "Navigation" },
    { value: "settings/landingpage", title: "Startseite" },
    { value: "settings/mimetypes", title: "Dateitypen" },
    { value: "settings/subscriptions", title: "Abos" },
    { value: "settings/priocategories", title: "Kategorien" },
    { value: "settings/ratecharts", title: "Tariftabellen" },
    { value: "settings/actions", title: "Aktionen" },
  ], [])

  return <TabsList tabs={tabs} defaultVal="settings" />
}
